import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Avatar, AvatarFallback } from './Avatar';
import type { Expert } from '../types/expert';
import type { Tier } from '../types/tier';
import { getInitials } from '../utils/expert';
import { nextTier, tierStyles } from '../utils/tier';
type TierLadderCardProps = {
  tier: Tier;
  members: Expert[];
  previousName?: string;
};
export function TierLadderCard({
  tier,
  members,
  previousName
}: TierLadderCardProps) {
  const style = tierStyles[tier.id];
  const isDark = tier.id === 'ambassador';
  const upper = nextTier(tier)?.minScore;
  const Icon = tier.icon;
  return <article className={`flex flex-col rounded-xl border ${isDark ? 'border-neutral-800 bg-neutral-900 text-white' : 'bg-card'}`} data-id="element-636" data-mp="3tntp8wc0ykbm">
      <div className="p-5" data-id="element-637" data-mp="cgrjpxhj368r">
        <div className="flex items-center justify-between" data-id="element-638" data-mp="cfy92lfg7wlc">
          <span className={`flex size-11 items-center justify-center rounded-lg ${style.solid}`} data-id="element-639" data-mp="7hepgrobuww5">
            <Icon className="size-5" aria-hidden="true" data-id="element-640" data-mp="9361niaqy5q5" />
          </span>
          <span className={`text-xs font-medium ${isDark ? 'text-amber-300' : 'text-muted-foreground'}`} data-id="element-641" data-mp="e17hzvgsfqxx">Tier {tier.level}</span>
        </div>
        <h3 className="mt-4 text-lg font-semibold" data-id="element-642" data-mp="i2aymk6ntr07">{tier.name}</h3>
        <p className={`text-xs ${isDark ? 'text-neutral-400' : 'text-muted-foreground'}`} lang="ar" data-id="element-643" data-mp="n2ovd62t9eix">{tier.nameAr}</p>
        <p className={`mt-2 text-sm ${isDark ? 'text-neutral-300' : 'text-muted-foreground'}`} data-id="element-644" data-mp="s076920e23tk">{tier.tagline}</p>
        <p className={`mt-3 inline-block rounded-md px-2 py-1 text-xs font-medium tabular-nums ${isDark ? 'bg-neutral-800 text-amber-300' : 'bg-muted'}`} data-id="element-645" data-mp="wz1xmscf90pa">
          Score {tier.minScore}{upper ? `–${upper - 0.1}` : '+'}
        </p>
      </div>

      <div className={`border-t px-5 py-4 ${isDark ? 'border-neutral-800' : ''}`} data-id="element-646" data-mp="15uhrh9d6ffuv">
        <p className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-neutral-400' : 'text-muted-foreground'}`} data-id="element-647" data-mp="1apn9hx3gkado">Criteria</p>
        <ul className="mt-2 space-y-1.5" data-id="element-648" data-mp="3bt7m2qh3d2g8">
          {tier.criteria.map(c => <li key={c} className={`text-xs ${isDark ? 'text-neutral-300' : 'text-muted-foreground'}`} data-id="element-649" data-mp="36v65cc751f7x">• {c}</li>)}
        </ul>
      </div>

      <div className={`flex-1 border-t px-5 py-4 ${isDark ? 'border-neutral-800' : ''}`} data-id="element-650" data-mp="3lldaxrc1r3r7">
        <p className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-neutral-400' : 'text-muted-foreground'}`} data-id="element-651" data-mp="3gsaijmj0a0cl">
          {previousName ? `Everything in ${previousName}, plus` : 'Privileges'}
        </p>
        <ul className="mt-2 space-y-2.5" data-id="element-652" data-mp="3qkyd0amijs85">
          {tier.privileges.map(p => <li key={p.title} className="flex gap-2 text-sm" data-id="element-653" data-mp="8cqtkzpdb43c">
              <CheckCircle2 className={`mt-0.5 size-4 shrink-0 ${isDark ? 'text-amber-300' : 'text-emerald-600'}`} aria-hidden="true" data-id="element-654" data-mp="3dw97tbr57h4" />
              <span data-id="element-655" data-mp="2a4vqyqpmytlg">
                <span className="font-medium" data-id="element-656" data-mp="2a6gligbyacjb">{p.title}</span>
                <span className={`block text-xs ${isDark ? 'text-neutral-400' : 'text-muted-foreground'}`} data-id="element-657" data-mp="256u6c1ckam7v">{p.description}</span>
              </span>
            </li>)}
        </ul>
      </div>

      <div className={`flex items-center justify-between border-t px-5 py-4 ${isDark ? 'border-neutral-800' : ''}`} data-id="element-658" data-mp="pl9vub0jkphk">
        <span className={`text-xs ${isDark ? 'text-neutral-400' : 'text-muted-foreground'}`} data-id="element-659" data-mp="axf16p2z4b1u">
          {members.length} expert{members.length === 1 ? '' : 's'}
        </span>
        <div className="flex -space-x-2" data-id="element-660" data-mp="1w7fd6hn92on">
          {members.slice(0, 4).map(m => <Link key={m.id} to={`/experts/${m.id}`} title={m.name} className="rounded-full" data-id="element-661" data-mp="6x4sv5rpl8jt">
              <Avatar size="sm" className={isDark ? 'ring-2 ring-neutral-900' : 'ring-2 ring-card'} data-id="element-662" data-mp="3o5a0l8ozabhw">
                <AvatarFallback className="text-[10px]" data-id="element-663" data-mp="3t4cv3z7c0zbm">{getInitials(m.name)}</AvatarFallback>
              </Avatar>
            </Link>)}
        </div>
      </div>
    </article>;
}